import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Hymn, SongFileExtension, SongSlug } from '../../../../../../common/src/model.ts';
import { useCustomMusic } from '../../../../data/use_custom_music.tsx';
import { EngineeringAdminWrapper } from '../shared.tsx';
import { EngineeringSongList, PDFLink } from './engineering_song_list.tsx';
import { useBrokenSongsForThisSunday, useForceRefreshMusicCaches } from './shared.tsx';
import { UploadButton } from './upload_button.tsx';

export const EngineeringCustomMusicPage = () => {
  const { customMusic, setIncludeAllOrgs, clearIncludeAllOrgs } = useCustomMusic();
  const { brokenSongsByOrg } = useBrokenSongsForThisSunday();
  const [showAllSongs, setShowAllSongs] = useState(false);
  useForceRefreshMusicCaches();

  useEffect(() => {
    setIncludeAllOrgs();
    return () => clearIncludeAllOrgs();
  }, [setIncludeAllOrgs, clearIncludeAllOrgs]);

  const showPDFLink = useCallback((hymn: Hymn) => {
    const xmlFileExists = !!hymn.extensions?.includes(SongFileExtension.Xml);
    const midiFileExists = !!hymn.extensions?.includes(SongFileExtension.Mid);
    return (!xmlFileExists && !midiFileExists) || (xmlFileExists && !!hymn.isPdfNewer);
  }, []);

  const isThisSunday = useCallback(
    (songSlug: SongSlug) => {
      return Boolean(Object.values(brokenSongsByOrg).find((hymns) => hymns.find(({ slug }) => slug === songSlug)));
    },
    [brokenSongsByOrg],
  );

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setShowAllSongs(event.target.checked);
  }, []);

  const hymnsByOrg = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(customMusic).map(([orgId, hymns]) => [
          orgId,
          hymns.filter((hymn) => showAllSongs || showPDFLink(hymn)),
        ]),
      ),
    [customMusic, showAllSongs, showPDFLink],
  );

  return (
    <EngineeringAdminWrapper title={'Custom Music'}>
      <OuterWrapper>
        <UploadButton />
        <Content key="content">
          Orgs colored <TestOrg>blue</TestOrg> are test orgs, ignore them. <br />
          Songs with a <PDFLink $highlight={true}>PDF link</PDFLink> do not yet have MusicXML files:
        </Content>
        <ShowAllSongsWrapper>
          <input type="checkbox" checked={showAllSongs} onChange={handleCheckboxChange} />
          <ShowAllSongs>Show all songs</ShowAllSongs>
        </ShowAllSongsWrapper>
        <EngineeringSongList
          key="song-list"
          hymnsByOrg={hymnsByOrg}
          showPDFLink={showPDFLink}
          isThisSunday={isThisSunday}
        />
      </OuterWrapper>
    </EngineeringAdminWrapper>
  );
};

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding-left: 20px;
`;

const Content = styled.div`
  margin: 20px 0;
`;

export const TestOrg = styled.p`
  color: deepskyblue;
  display: inline;
`;

const ShowAllSongsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
`;

const ShowAllSongs = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.6rem;
`;
